import type { FormEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion as Motion, AnimatePresence } from "motion/react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

const SIZE_STYLES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
};

interface DrawerModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  size?: "sm" | "md" | "lg" | "xl";
  side?: "right" | "left";
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void | Promise<void>;
  isSubmitting?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  hideFooter?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function DrawerModal({
  open,
  onClose,
  title,
  description,
  size = "lg",
  side = "right",
  onSubmit,
  isSubmitting = false,
  submitLabel = "Valider",
  cancelLabel = "Annuler",
  submitDisabled = false,
  hideFooter = false,
  footer,
  children,
  className,
}: DrawerModalProps) {
  const isRight = side === "right";

  const header = (
    <header className="flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-6">
      <div>
        <h3 className="font-display text-2xl font-black uppercase tracking-tight text-[#003366]">
          {title}
        </h3>
        {description && <p className="mt-1 text-sm font-medium text-gray-500">{description}</p>}
      </div>
      <IconButton icon={X} label="Fermer" onClick={onClose} disabled={isSubmitting} />
    </header>
  );

  const body = <div className="flex-1 overflow-y-auto bg-[#F8FAFC] px-8 py-6">{children}</div>;

  const footerNode = !hideFooter && (
    <footer className="flex items-center justify-end gap-2 border-t border-gray-100 bg-white px-8 py-5">
      {footer ?? (
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {cancelLabel}
          </Button>
          <Button
            type={onSubmit ? "submit" : "button"}
            disabled={submitDisabled}
            loading={isSubmitting}
          >
            {submitLabel}
          </Button>
        </>
      )}
    </footer>
  );

  const containerClass = "flex h-full flex-col overflow-hidden";

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] overflow-hidden">
          <Motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
            onClick={isSubmitting ? undefined : onClose}
          />

          <Motion.div
            key="drawer"
            initial={{ x: isRight ? "100%" : "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: isRight ? "100%" : "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={cn(
              "absolute inset-y-0 w-full bg-white shadow-2xl",
              isRight ? "right-0" : "left-0",
              SIZE_STYLES[size],
              className,
            )}
          >
            {onSubmit ? (
              <form onSubmit={onSubmit} className={containerClass}>
                {header}
                {body}
                {footerNode}
              </form>
            ) : (
              <div className={containerClass}>
                {header}
                {body}
                {footerNode}
              </div>
            )}
          </Motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
